import React, { type ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'
import { HelpIcon } from '@/shared/components/HelpIcon'
import type { ModalName } from '@/lib/modalTelemetry'
import { useMediaQuery } from '@/lib/hooks'
import { formatCount } from '@/lib/format'

const TREND_WINDOW_DAYS = 90

/** Fixed categorical hue order (global.css --chart-*): assigned to entities by their
 *  total-descending order from the worker, never cycled or re-ranked client-side. */
const TREND_SERIES_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)']

interface TrendEntity {
  name: string
  total: number
  series: Array<{ date: string; count: number }>
}

/**
 * Multi-series line chart for the top-5 trend cards: one line per module/workspace over a
 * shared 90-day domain. The worker omits zero days, so each entity is zero-filled against
 * the domain here. Legend hover isolates one line (the dataviz secondary encoding for the
 * light-theme palette's CVD floor band); day hover shows a per-entity readout.
 */
export function TopTrendChart({
  kicker,
  title,
  entities,
  help,
  helpTrack,
  unit,
  legendHref,
}: {
  kicker: string
  title: string
  entities: TrendEntity[]
  help?: ReactNode
  /** Telemetry name for the `help` dialog - per card, since one component renders both charts. */
  helpTrack: ModalName
  unit: string
  /** When set, legend rows become links to their artifact (module / workspace) while keeping
   *  the hover-isolation behaviour. */
  legendHref?: (name: string) => string
}) {
  // Shared UTC date domain, oldest -> today. The worker buckets by strftime/'unixepoch'
  // (UTC days), so the client domain must be UTC too - never locale days.
  const DAY_MS = 86400000
  const domain = Array.from({ length: TREND_WINDOW_DAYS }, (_, i) =>
    new Date(Date.now() - (TREND_WINDOW_DAYS - 1 - i) * DAY_MS).toISOString().slice(0, 10),
  )

  const shown = entities.slice(0, TREND_SERIES_COLORS.length)
  const lines = shown.map((e, idx) => {
    const byDate = new Map(e.series.map((p) => [p.date, p.count]))
    return {
      name: e.name,
      total: e.total,
      color: TREND_SERIES_COLORS[idx],
      values: domain.map((d) => byDate.get(d) ?? 0),
    }
  })

  const max = Math.max(...lines.flatMap((l) => l.values), 1)
  const grandTotal = lines.reduce((s, l) => s + l.total, 0)

  const [hovered, setHovered] = useState<number | null>(null) // day index
  const [focused, setFocused] = useState<number | null>(null) // legend-hovered entity index
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const fadeTransition = reduceMotion ? 'none' : 'opacity 0.12s'

  // MM-DD from the stored ISO date string ("2026-07-12" -> "07-12"), locale-free
  // and no Date parsing. Used for the x-axis ticks and the hover readout.
  const shortDate = (d: string) => (d.length >= 10 ? d.slice(5) : d)

  // ── Line geometry ──────────────────────────────────────────────────────────
  // Fixed viewBox; the SVG stretches to the column width via CSS. Y is inverted
  // (SVG origin is top-left) and padded so the stroke/dots don't clip at the edges.
  const VB_W = 300
  const VB_H = 100
  const PAD_Y = 8
  const n = TREND_WINDOW_DAYS
  const xAt = (i: number) => (i / (n - 1)) * VB_W
  const yAt = (c: number) => VB_H - PAD_Y - (c / max) * (VB_H - PAD_Y * 2)
  const pathFor = (values: number[]) =>
    values.map((c, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(c).toFixed(1)}`).join(' ')

  // Evenly-spaced date ticks so the X axis is readable (labelling all 90 overlaps).
  // ~6 ticks across the window, always including first and last.
  const tickCount = 6
  const tickIdx = Array.from({ length: tickCount }, (_, k) => Math.round((k / (tickCount - 1)) * (n - 1)))
  const uniqTickIdx = Array.from(new Set(tickIdx))

  return (
    <div className="card fu">
      <div className="card-header">
        <div>
          <div className="kicker">{kicker}</div>
          <h3 style={{ fontSize: '0.95rem' }}>{title}</h3>
        </div>
        {help && (
          <HelpIcon track={helpTrack} title={title} kicker="// what this implies">
            {help}
          </HelpIcon>
        )}
      </div>
      <div style={{ padding: '16px 18px' }}>
        {lines.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">▲</div>
            <div>No data yet.</div>
          </div>
        )}
        {lines.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 16 }}>
            {/* Line graph (left) + dated x-axis */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {/* Y-axis value range: max at top -> 0 at bottom. */}
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 92, fontFamily: 'var(--mono)', fontSize: '0.55rem', color: 'var(--text3)', textAlign: 'right', flexShrink: 0 }}>
                  <span>{formatCount(max)}</span>
                  <span>{formatCount(Math.round(max / 2))}</span>
                  <span>0</span>
                </div>
                <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                  {/* Hovered-day readout: date + one row per entity. HTML (not SVG text)
                      so it stays crisp under the non-uniform SVG stretch. */}
                  {hovered !== null && (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${(hovered / (n - 1)) * 100}%`,
                        top: -2,
                        transform: `translateX(${hovered < n * 0.15 ? '0' : hovered > n * 0.85 ? '-100%' : '-50%'})`,
                        fontFamily: 'var(--mono)',
                        fontSize: '0.58rem',
                        color: 'var(--text)',
                        background: 'var(--bg2)',
                        border: '1px solid var(--border)',
                        borderRadius: 4,
                        padding: '4px 8px',
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        zIndex: 1,
                      }}
                    >
                      <div style={{ color: 'var(--text3)', marginBottom: 2 }}>{shortDate(domain[hovered])}</div>
                      {lines.map((l) => (
                        <div key={l.name} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ width: 6, height: 6, borderRadius: 2, background: l.color, flexShrink: 0 }} />
                          <span style={{ color: 'var(--text2)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name}</span>
                          <strong style={{ marginLeft: 'auto', paddingLeft: 8 }}>{formatCount(l.values[hovered])}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                  <svg
                    viewBox={`0 0 ${VB_W} ${VB_H}`}
                    preserveAspectRatio="none"
                    width="100%"
                    height={92}
                    style={{ display: 'block', overflow: 'visible' }}
                    onMouseLeave={() => setHovered(null)}
                  >
                    {/* One 2px line per entity. vectorEffect keeps stroke width constant
                        despite the non-uniform preserveAspectRatio stretch. */}
                    {lines.map((l, i) => (
                      <path
                        key={l.name}
                        d={pathFor(l.values)}
                        fill="none"
                        stroke={l.color}
                        strokeWidth={2}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        vectorEffect="non-scaling-stroke"
                        opacity={focused !== null && focused !== i ? 0.22 : 1}
                        style={{ transition: fadeTransition }}
                      />
                    ))}
                    {/* Active crosshair + one dot per entity for the hovered day. */}
                    {hovered !== null && (
                      <>
                        <line
                          x1={xAt(hovered)} y1={0} x2={xAt(hovered)} y2={VB_H}
                          stroke="var(--text3)" strokeWidth={1} vectorEffect="non-scaling-stroke" strokeDasharray="2 2"
                        />
                        {lines.map((l, i) => (
                          <circle
                            key={l.name}
                            cx={xAt(hovered)} cy={yAt(l.values[hovered])}
                            r={3}
                            fill={l.color}
                            stroke="var(--bg)"
                            strokeWidth={1.5}
                            vectorEffect="non-scaling-stroke"
                            opacity={focused !== null && focused !== i ? 0.22 : 1}
                          />
                        ))}
                      </>
                    )}
                    {/* Invisible full-height hit strips: a hover target per day so the
                        thin lines are easy to select. */}
                    {domain.map((d, i) => (
                      <rect
                        key={`hit-${d}`}
                        x={i === 0 ? 0 : (xAt(i - 1) + xAt(i)) / 2}
                        y={0}
                        width={
                          (i === 0 ? xAt(0) : (xAt(i) - xAt(i - 1)) / 2) +
                          (i === n - 1 ? VB_W - xAt(i) : (xAt(i + 1) - xAt(i)) / 2)
                        }
                        height={VB_H}
                        fill="transparent"
                        style={{ cursor: 'default' }}
                        onMouseEnter={() => setHovered(i)}
                      />
                    ))}
                  </svg>
                  {/* Dated x-axis: evenly-spaced ticks across the window. */}
                  <div style={{ position: 'relative', height: 12, marginTop: 6 }}>
                    {uniqTickIdx.map((idx) => (
                      <span
                        key={idx}
                        style={{
                          position: 'absolute',
                          left: `${(idx / (n - 1)) * 100}%`,
                          transform: idx === 0 ? 'translateX(0)' : idx === n - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                          fontFamily: 'var(--mono)',
                          fontSize: '0.55rem',
                          color: 'var(--text3)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {shortDate(domain[idx])}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {/* Legend (right): headline total + one row per entity with its 90d total.
                Hovering a row isolates that line. Text wears text tokens; the dot
                carries series identity. */}
            <div style={{ width: 148, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6, borderLeft: '1px solid var(--border)', paddingLeft: 14 }}>
              <div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: '0.55rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)' }}>
                  {unit} · top {lines.length} · 90d
                </div>
                <div style={{ fontFamily: 'var(--head)', fontWeight: 700, fontSize: '1.15rem', color: 'var(--text)', lineHeight: 1.15 }}>
                  {formatCount(grandTotal)}
                </div>
              </div>
              {lines.map((l, i) => {
                const isDimmed = focused !== null && focused !== i
                const legendRowStyle: React.CSSProperties = {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  width: '100%',
                  textAlign: 'left',
                  background: focused === i ? 'var(--bg3)' : 'transparent',
                  border: 'none',
                  borderRadius: 4,
                  padding: '2px 4px',
                  cursor: legendHref ? 'pointer' : 'default',
                  textDecoration: 'none',
                  opacity: isDimmed ? 0.4 : 1,
                  transition: fadeTransition,
                }
                const body = (
                  <>
                    <span style={{ width: 7, height: 7, borderRadius: 2, background: l.color, flexShrink: 0 }} />
                    <span style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.name}
                    </span>
                    <span style={{ fontFamily: 'var(--head)', fontSize: '0.72rem', color: 'var(--text)', marginLeft: 'auto', paddingLeft: 6 }}>
                      {formatCount(l.total)}
                    </span>
                  </>
                )
                const hoverHandlers = {
                  onMouseEnter: () => setFocused(i),
                  onMouseLeave: () => setFocused(null),
                  onFocus: () => setFocused(i),
                  onBlur: () => setFocused(null),
                }
                return legendHref ? (
                  <Link key={l.name} to={legendHref(l.name)} title={l.name} style={legendRowStyle} {...hoverHandlers}>
                    {body}
                  </Link>
                ) : (
                  <button key={l.name} type="button" title={l.name} style={legendRowStyle} {...hoverHandlers}>
                    {body}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

